import React, { useState } from 'react';
import { User, Mail, HardDrive, Send, FileText, Settings, Crown, Shield, Eye, EyeOff } from 'lucide-react';

export default function UserProfile() {
  const [theme, setTheme] = useState('light');
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  // Modal states
  const [activeModal, setActiveModal] = useState(null);
  
  // Form states
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [passwordForm, setPasswordForm] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [emailPrefs, setEmailPrefs] = useState({
    marketing: true,
    updates: true,
    security: true
  });
  
  // Mock user data
  const [user, setUser] = useState({
    name: 'John Doe',
    email: 'john.doe@example.com',
    accountType: 'premium',
    storageUsed: 3.2,
    storageLimit: 5,
    filesStored: 247,
    emailsSent: 89,
    memberSince: 'Jan 2024'
  });

  const accountTypes = {
    basico: { name: 'Básico', limit: 2, color: 'secondary', icon: User },
    premium: { name: 'Premium', limit: 5, color: 'primary', icon: Crown },
    enterprise: { name: 'Enterprise', limit: 10, color: 'info', icon: Shield },
    admin: { name: 'Admin', limit: 100, color: 'dark', icon: Shield }
  };

  const currentAccount = accountTypes[user.accountType];
  const storagePercent = (user.storageUsed / user.storageLimit) * 100;
  const AccountIcon = currentAccount.icon;

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    document.documentElement.setAttribute('data-bs-theme', newTheme);
  };

  const openModal = (modalName) => {
    if (modalName === 'edit') {
      setEditForm({ name: user.name, email: user.email });
    }
    setActiveModal(modalName);
  };

  const closeModal = () => {
    setActiveModal(null);
    setPasswordForm({ current: '', new: '', confirm: '' });
  };

  const handleEditProfile = (e) => {
    e.preventDefault();
    setUser({ ...user, name: editForm.name, email: editForm.email });
    closeModal();
    // Here you'd make an API call to update the user
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    if (passwordForm.new !== passwordForm.confirm) {
      alert('New passwords do not match!');
      return;
    }
    // Here you'd make an API call to change password
    alert('Password changed successfully!');
    closeModal();
  };

  const handleEmailPreferences = (e) => {
    e.preventDefault();
    // Here you'd make an API call to update preferences
    alert('Email preferences updated!');
    closeModal();
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      // Here you'd make an API call to delete account
      alert('Account deletion initiated.');
      closeModal();
    }
  };

  const Modal = ({ show, onClose, title, children, size = 'modal-md' }) => {
    if (!show) return null;
    
    return (
      <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className={`modal-dialog modal-dialog-centered ${size}`}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            {children}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-bs-theme={theme} style={{ minHeight: '100vh', backgroundColor: 'var(--bs-body-bg)' }}>
      <div className="container py-5">
        {/* Header with Theme Toggle */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="mb-0">My Profile</h1>
          <button 
            className="btn btn-outline-secondary" 
            onClick={toggleTheme}
          >
            {theme === 'light' ? '🌙' : '☀️'}
          </button>
        </div>

        <div className="row g-4">
          {/* Left Column - User Info & Storage */}
          <div className="col-lg-4">
            {/* User Card */}
            <div className="card shadow-sm mb-4">
              <div className="card-body text-center">
                <div 
                  className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: '100px',
                    height: '100px',
                    background: 'linear-gradient(90deg, #29abe2, #2e3192, #1b1464)',
                    color: 'white',
                    fontSize: '2.5rem'
                  }}
                >
                  {user.name.charAt(0)}
                </div>
                <h4 className="mb-1">{user.name}</h4>
                <p className="text-muted mb-3">
                  <Mail size={14} className="me-1" />
                  {user.email}
                </p>
                <span className={`badge bg-${currentAccount.color} d-inline-flex align-items-center gap-1 px-3 py-2`}>
                  <AccountIcon size={16} />
                  {currentAccount.name}
                </span>
                <p className="text-muted mt-2 mb-0 small">Member since {user.memberSince}</p>
              </div>
            </div>

            {/* Storage Card */}
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <HardDrive size={20} className="me-2" />
                  <h5 className="mb-0">Storage Usage</h5>
                </div>
                <div className="mb-2">
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted small">Used Space</span>
                    <span className="fw-bold">{user.storageUsed} GB / {user.storageLimit} GB</span>
                  </div>
                  <div className="progress" style={{ height: '10px' }}>
                    <div 
                      className="progress-bar" 
                      role="progressbar" 
                      style={{ 
                        width: `${storagePercent}%`,
                        background: storagePercent > 80 ? '#dc3545' : 'linear-gradient(90deg, #29abe2, #2e3192, #1b1464)'
                      }}
                      aria-valuenow={storagePercent} 
                      aria-valuemin="0" 
                      aria-valuemax="100"
                    ></div>
                  </div>
                </div>
                {storagePercent > 80 && (
                  <div className="alert alert-warning py-2 px-3 mt-3 mb-0 small">
                    You're running low on storage. Consider upgrading!
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Stats & Subscription */}
          <div className="col-lg-8">
            {/* Activity Stats */}
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div 
                        className="rounded p-3 me-3"
                        style={{ background: 'linear-gradient(90deg, #29abe2, #2e3192, #1b1464)' }}
                      >
                        <FileText size={24} color="white" />
                      </div>
                      <div>
                        <h3 className="mb-0">{user.filesStored}</h3>
                        <p className="text-muted mb-0 small">Files Stored</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div 
                        className="rounded p-3 me-3"
                        style={{ background: 'linear-gradient(90deg, #29abe2, #2e3192, #1b1464)' }}
                      >
                        <Send size={24} color="white" />
                      </div>
                      <div>
                        <h3 className="mb-0">{user.emailsSent}</h3>
                        <p className="text-muted mb-0 small">Emails Sent</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Plans */}
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <h5 className="card-title mb-4">Subscription Plans</h5>
                <div className="row g-3">
                  {Object.entries(accountTypes).filter(([key]) => key !== 'admin').map(([key, plan]) => (
                    <div className="col-md-4" key={key}>
                      <div className={`card h-100 ${user.accountType === key ? 'border-primary' : ''}`}>
                        <div className="card-body">
                          <div className="d-flex align-items-center mb-2">
                            <plan.icon size={20} className="me-2" />
                            <h6 className="mb-0">{plan.name}</h6>
                          </div>
                          <h4 className="mb-1">{plan.limit} GB</h4>
                          <p className="text-muted small mb-3">
                            {key === 'basico' ? 'Free' : key === 'premium' ? '$9.99/mo' : '$19.99/mo'}
                          </p>
                          {user.accountType === key ? (
                            <button className="btn btn-outline-success w-100" disabled>
                              Current Plan
                            </button>
                          ) : (
                            <button 
                              className="btn w-100"
                              style={{ 
                                background: key === 'basico' ? '#6c757d' : 'linear-gradient(270deg, #e1e9f6, #ebe5f6)',
                                border: 'none',
                                color: '#333'
                              }}
                            >
                              {accountTypes[user.accountType].limit < plan.limit ? 'Upgrade' : 'Downgrade'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Account Settings */}
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <Settings size={20} className="me-2" />
                  <h5 className="mb-0">Account Settings</h5>
                </div>
                <div className="list-group list-group-flush">
                  <button 
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    onClick={() => openModal('edit')}
                  >
                    Edit Profile
                    <span>›</span>
                  </button>
                  <button 
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    onClick={() => openModal('password')}
                  >
                    Change Password
                    <span>›</span>
                  </button>
                  <button 
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    onClick={() => openModal('email')}
                  >
                    Email Preferences
                    <span>›</span>
                  </button>
                  <button 
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center text-danger"
                    onClick={() => openModal('delete')}
                  >
                    Delete Account
                    <span>›</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <Modal show={activeModal === 'edit'} onClose={closeModal} title="Edit Profile">
        <form onSubmit={handleEditProfile}>
          <div className="modal-body">
            <div className="mb-3">
              <label htmlFor="editName" className="form-label">Name</label>
              <input
                type="text"
                className="form-control"
                id="editName"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="editEmail" className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                id="editEmail"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeModal}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn text-white"
              style={{ background: 'linear-gradient(90deg, #29abe2, #2e3192, #1b1464)', border: 'none' }}
            >
              Save Changes
            </button>
          </div>
        </form>
      </Modal>

      {/* Change Password Modal */}
      <Modal show={activeModal === 'password'} onClose={closeModal} title="Change Password">
        <form onSubmit={handleChangePassword}>
          <div className="modal-body">
            <div className="mb-3">
              <label htmlFor="currentPassword" className="form-label">Current Password</label>
              <div className="input-group">
                <input
                  type={showPassword.current ? "text" : "password"}
                  className="form-control"
                  id="currentPassword"
                  value={passwordForm.current}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                  required
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setShowPassword({ ...showPassword, current: !showPassword.current })}
                >
                  {showPassword.current ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="newPassword" className="form-label">New Password</label>
              <div className="input-group">
                <input
                  type={showPassword.new ? "text" : "password"}
                  className="form-control"
                  id="newPassword"
                  value={passwordForm.new}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                  required
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setShowPassword({ ...showPassword, new: !showPassword.new })}
                >
                  {showPassword.new ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
              <div className="input-group">
                <input
                  type={showPassword.confirm ? "text" : "password"}
                  className="form-control"
                  id="confirmPassword"
                  value={passwordForm.confirm}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                  required
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setShowPassword({ ...showPassword, confirm: !showPassword.confirm })}
                >
                  {showPassword.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeModal}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn text-white"
              style={{ background: 'linear-gradient(90deg, #29abe2, #2e3192, #1b1464)', border: 'none' }}
            >
              Change Password
            </button>
          </div>
        </form>
      </Modal>

      {/* Email Preferences Modal */}
      <Modal show={activeModal === 'email'} onClose={closeModal} title="Email Preferences">
        <form onSubmit={handleEmailPreferences}>
          <div className="modal-body">
            <p className="text-muted mb-3">Choose which emails you'd like to receive:</p>
            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="marketing"
                checked={emailPrefs.marketing}
                onChange={(e) => setEmailPrefs({ ...emailPrefs, marketing: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="marketing">
                <strong>Marketing & Promotions</strong>
                <br />
                <small className="text-muted">New features, special offers, and updates</small>
              </label>
            </div>
            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="updates"
                checked={emailPrefs.updates}
                onChange={(e) => setEmailPrefs({ ...emailPrefs, updates: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="updates">
                <strong>Product Updates</strong>
                <br />
                <small className="text-muted">Important service announcements and changes</small>
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="security"
                checked={emailPrefs.security}
                onChange={(e) => setEmailPrefs({ ...emailPrefs, security: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="security">
                <strong>Security Alerts</strong>
                <br />
                <small className="text-muted">Account security notifications (recommended)</small>
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeModal}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn text-white"
              style={{ background: 'linear-gradient(90deg, #29abe2, #2e3192, #1b1464)', border: 'none' }}
            >
              Save Preferences
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Account Modal */}
      <Modal show={activeModal === 'delete'} onClose={closeModal} title="Delete Account" size="modal-sm">
        <div className="modal-body">
          <div className="text-center mb-3">
            <div 
              className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
              style={{ width: '60px', height: '60px', backgroundColor: '#dc3545' }}
            >
              <span style={{ fontSize: '2rem' }}>⚠️</span>
            </div>
            <h5>Are you sure?</h5>
            <p className="text-muted">
              This action cannot be undone. All your files and data will be permanently deleted.
            </p>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={closeModal}>
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-danger"
            onClick={handleDeleteAccount}
          >
            Delete Account
          </button>
        </div>
      </Modal>
    </div>
  );
}